import { LazyQueryExecFunction, OperationVariables } from '@parkchirp/api-client';
import { Dispatch, SetStateAction } from 'react';

import { SnackbarParams } from '../types';
import { FILE_UPLOAD_MAXIMUM } from './constants';
import { dayjs } from './dayjs';

export type FileUploadTableFile = {
	id: number;
	fileName: string;
	bucketPath: string;
	createdAt: string;
};

export async function addFileUploadTableFiles(
	newFiles: FileList,
	files: FileUploadTableFile[],
	bucket: string,
	bucketDir: string,
	includeTimestamp: boolean,
	getPutSignedUrl: LazyQueryExecFunction<any, OperationVariables>,
	setUploadingFiles: ((x: string[]) => void) & ((x: (y: string[]) => string[]) => void),
	setFiles: (x: FileUploadTableFile[]) => void,
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackbarParams> | null>>,
) {
	const uploadTimestamp = dayjs().unix();

	const incomingFiles = [...newFiles];

	if (incomingFiles.length + files.length > FILE_UPLOAD_MAXIMUM) {
		setShowSnackbar({
			message: `Unable to add file(s): Max limit of 15 files has been reached.`,
			severity: 'warning',
		});
		return;
	}

	const newFileRows = incomingFiles.map((file, idx) => ({
		id: files.length + idx,
		fileName: includeTimestamp ? `${uploadTimestamp}-${file.name}` : file.name,
		bucketPath: '',
		createdAt: dayjs().format('YYYY-MM-DD'),
	}));

	setUploadingFiles(newFileRows.map(({ fileName }) => fileName));
	setFiles([...files, ...newFileRows]);

	try {
		await Promise.all(
			incomingFiles.map(async (file) => {
				const fileName = includeTimestamp ? `${uploadTimestamp}-${file.name}` : file.name;

				const putSignedUrlResponse = await getPutSignedUrl({
					variables: {
						bucket,
						s3Key: `${bucketDir}/${fileName}`,
					},
					context: {
						apiVersion: 'v2',
					},
					fetchPolicy: 'network-only',
				});

				const putSignedUrl = putSignedUrlResponse.data.getPutSignedUrl.url;

				await fetch(putSignedUrl, {
					method: 'PUT',
					headers: { 'Content-Type': file.type },
					body: file,
				});

				setUploadingFiles((prevFiles) => prevFiles.filter((uploadedFile) => uploadedFile !== fileName));
			}),
		);
	} catch (error) {
		setShowSnackbar({
			message: `Unable to add file(s): ${error.message}`,
			severity: 'error',
		});
	}
}

export const downloadCSVFile = (csvData, fileName) => {
	const CSVFile = new Blob([csvData], { type: 'text/csv' });
	const tempLink = document.createElement('a');
	tempLink.download = `${fileName}.csv`;
	const url = window.URL.createObjectURL(CSVFile);
	tempLink.href = url;
	tempLink.style.display = 'none';
	document.body.appendChild(tempLink);
	tempLink.click();
	document.body.removeChild(tempLink);
};

export const uploadDocToS3 = async (url: string, file: File): Promise<boolean> => {
	try {
		const objectURL = URL.createObjectURL(file);
		const documentBlob = await fetch(objectURL).then((r) => r.blob());

		const response = await fetch(url, {
			method: 'PUT',
			headers: { 'Content-Type': file.type },
			body: documentBlob,
		});

		if (response.ok) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};
